import { element } from './kit.js';
import { uiText } from './text.js';

/**
 * The world map on a phone (ADR 0086): the whole screen, the land drawn
 * large enough to read its names, moved by a finger and zoomed by whole
 * steps. This file holds the gestures and the arithmetic; `world-map.ts`
 * draws the map at the scale chosen here.
 */

/** The scale the map opens at, in CSS pixels a tile: a region and all its names. */
export const EXPLORE_SCALE = 2;
/** The closest step: past it a phone's canvas grows heavy for nothing. */
const EXPLORE_MAX_SCALE = 3;
/** How far two fingers must spread (or close) for one zoom step. */
const PINCH_STEP = 1.3;
/** A press that travels further is a drag, not a tap, in CSS pixels. */
const TAP_SLOP = 6;
const WHEEL_PAUSE_MS = 250;

/**
 * The smallest scale whose land covers the view on both axes, in whole
 * device pixels a tile so the canvas stays crisp: no band of empty screen
 * beside the world, the rest a drag away.
 */
export function coverScale(
  view: { width: number; height: number },
  world: { width: number; height: number },
  dpr: number,
): number {
  const room = Math.max(view.width / world.width, view.height / world.height);
  return Math.max(1, Math.ceil(room * dpr - 1e-9)) / dpr;
}

/**
 * The zoom steps: the land covering the view, then each whole CSS pixel a
 * tile above it, up to EXPLORE_MAX_SCALE (one step more on a view so large
 * the cover is already past it).
 */
export function zoomLevels(cover: number): number[] {
  const levels = [cover];
  const top = Math.max(EXPLORE_MAX_SCALE, Math.floor(cover) + 1);
  for (let scale = 1; scale <= top; scale += 1) {
    if (scale > cover) levels.push(scale);
  }
  return levels;
}

/** The step nearest to a wanted scale. */
export function levelNear(levels: readonly number[], scale: number): number {
  let best = 0;
  for (let k = 1; k < levels.length; k += 1) {
    if (Math.abs((levels[k] ?? 0) - scale) < Math.abs((levels[best] ?? 0) - scale)) best = k;
  }
  return best;
}

/**
 * The offset of the map along one axis, kept so the land always covers the
 * screen; a map narrower than the screen sits in its middle.
 */
export function clampOffset(offset: number, size: number, view: number): number {
  if (size <= view) return Math.round((view - size) / 2);
  return Math.round(Math.min(0, Math.max(view - size, offset)));
}

/** A pinch's zoom step so far: +1 spread, -1 closed, 0 not yet. */
export function pinchStep(start: number, now: number): -1 | 0 | 1 {
  if (start <= 0) return 0;
  const ratio = now / start;
  if (ratio >= PINCH_STEP) return 1;
  if (ratio <= 1 / PINCH_STEP) return -1;
  return 0;
}

interface Point {
  x: number;
  y: number;
}

/**
 * The finger (or the mouse) over the explorer's stage: a drag moves the
 * surface, two fingers zoom by steps, the wheel too. A drag swallows the
 * click that ends it, so a game master's teleport or a quiet mark only
 * answers a tap.
 */
export class MapPan {
  /** The surface's offset in the stage, CSS pixels. */
  x = 0;
  y = 0;
  enabled = false;
  private readonly pointers = new Map<number, Point>();
  private drag: { from: Point; at: Point } | undefined;
  private pinchFrom = 0;
  private travelled = 0;
  private lastWheel = -Infinity;

  constructor(
    stage: HTMLElement,
    private readonly surface: HTMLElement,
    private readonly onZoom: (step: -1 | 1, focus: Point) => void,
    /** The stage's part left uncovered, which the land must fill. */
    private readonly view: () => { width: number; height: number },
  ) {
    stage.addEventListener('pointerdown', this.onDown);
    stage.addEventListener('wheel', this.onWheel, { passive: false });
    stage.addEventListener(
      'click',
      (event) => {
        if (this.travelled <= TAP_SLOP) return;
        event.stopImmediatePropagation();
        event.preventDefault();
      },
      true,
    );
  }

  /** Whether the press that just ended moved the map. */
  get dragged(): boolean {
    return this.travelled > TAP_SLOP;
  }

  /** Puts the surface at an offset, kept over the screen. */
  moveTo(x: number, y: number): void {
    const { width, height } = this.view();
    this.x = clampOffset(x, this.surface.offsetWidth, width);
    this.y = clampOffset(y, this.surface.offsetHeight, height);
    this.surface.style.transform = `translate(${String(this.x)}px, ${String(this.y)}px)`;
  }

  /** The surface back in place, for the framed map of a large screen. */
  reset(): void {
    this.x = 0;
    this.y = 0;
    this.surface.style.transform = '';
  }

  destroy(): void {
    this.release();
  }

  private readonly onDown = (event: PointerEvent): void => {
    if (!this.enabled || (event.pointerType === 'mouse' && event.button !== 0)) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 1) {
      this.travelled = 0;
      this.drag = { from: { x: this.x, y: this.y }, at: { x: event.clientX, y: event.clientY } };
      // On the window, not captured by the stage: a capture would steal the
      // tap from a quiet mark's own listener.
      window.addEventListener('pointermove', this.onMove);
      window.addEventListener('pointerup', this.onUp);
      window.addEventListener('pointercancel', this.onUp);
    } else if (this.pointers.size === 2) {
      this.drag = undefined;
      this.travelled = TAP_SLOP + 1;
      this.pinchFrom = this.spread();
    }
  };

  private readonly onMove = (event: PointerEvent): void => {
    if (!this.pointers.has(event.pointerId)) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size >= 2) {
      const step = pinchStep(this.pinchFrom, this.spread());
      if (step === 0) return;
      this.pinchFrom = this.spread();
      this.onZoom(step, this.middle());
      return;
    }
    const drag = this.drag;
    if (drag === undefined) return;
    const dx = event.clientX - drag.at.x;
    const dy = event.clientY - drag.at.y;
    this.travelled = Math.max(this.travelled, Math.hypot(dx, dy));
    if (!this.dragged) return;
    this.moveTo(drag.from.x + dx, drag.from.y + dy);
  };

  private readonly onUp = (event: PointerEvent): void => {
    this.pointers.delete(event.pointerId);
    if (this.pointers.size > 0) {
      // From two fingers back to one: the drag starts again where it is.
      const [rest] = this.pointers.values();
      if (rest !== undefined) this.drag = { from: { x: this.x, y: this.y }, at: rest };
      return;
    }
    this.drag = undefined;
    this.release();
  };

  private readonly onWheel = (event: WheelEvent): void => {
    if (!this.enabled || event.deltaY === 0) return;
    event.preventDefault();
    // A touchpad sends a burst of small wheels: one step per burst.
    if (event.timeStamp - this.lastWheel < WHEEL_PAUSE_MS) return;
    this.lastWheel = event.timeStamp;
    this.onZoom(event.deltaY < 0 ? 1 : -1, { x: event.clientX, y: event.clientY });
  };

  private release(): void {
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
  }

  private spread(): number {
    const [a, b] = this.pointers.values();
    return a === undefined || b === undefined ? 0 : Math.hypot(a.x - b.x, a.y - b.y);
  }

  private middle(): Point {
    const [a, b] = this.pointers.values();
    if (a === undefined || b === undefined) return { x: 0, y: 0 };
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }
}

/** The panel's tabs: the legend's three parts, one at a time on a phone. */
export const EXPLORE_TABS = [
  { id: 'regions', label: 'Régions' },
  { id: 'events', label: 'Événements' },
  { id: 'keys', label: 'Légende' },
] as const;
export type ExploreTab = (typeof EXPLORE_TABS)[number]['id'];

export interface ExploreControls {
  /** Zoom in, zoom out, back to the player: on the right, over the map. */
  zoom: HTMLElement;
  zoomIn: HTMLButtonElement;
  zoomOut: HTMLButtonElement;
  recentre: HTMLButtonElement;
  /** Where the player stands, at the bottom; a tap opens the panel. */
  here: HTMLButtonElement;
  hereChip: HTMLElement;
  hereName: HTMLElement;
  hereTag: HTMLElement;
  /** The panel's tabs, heading the legend's column. */
  tabs: HTMLElement;
}

function controlButton(className: string, label: string): HTMLButtonElement {
  const button = element('button', `world-map__control ${className}`);
  button.type = 'button';
  button.title = uiText(label);
  button.setAttribute('aria-label', uiText(label));
  return button;
}

export function exploreControls(onTab: (tab: ExploreTab) => void): ExploreControls {
  const zoom = element('div', 'world-map__zoom');
  const zoomIn = controlButton('world-map__control--in', 'Zoomer');
  const zoomOut = controlButton('world-map__control--out', 'Dézoomer');
  const recentre = controlButton('world-map__control--self', 'Me recentrer');
  zoom.append(zoomIn, zoomOut, recentre);

  const here = element('button', 'world-map__here');
  here.type = 'button';
  here.setAttribute('aria-label', uiText('Régions'));
  const hereChip = element('span', 'world-map__chip');
  const hereName = element('span', 'world-map__here-name');
  const hereTag = element('span', 'world-map__tag');
  here.append(hereChip, hereName, hereTag, element('span', 'world-map__here-arrow'));

  const tabs = element('div', 'world-map__tabs');
  tabs.setAttribute('role', 'tablist');
  for (const tab of EXPLORE_TABS) {
    const button = element('button', 'world-map__tab', tab.label);
    button.type = 'button';
    button.dataset.tab = tab.id;
    button.setAttribute('role', 'tab');
    button.addEventListener('click', () => {
      onTab(tab.id);
    });
    tabs.append(button);
  }
  return { zoom, zoomIn, zoomOut, recentre, here, hereChip, hereName, hereTag, tabs };
}
